import type React from "react";
import { useTranslation } from "react-i18next";
import { CloseIcon, PauseIcon, PlayIcon, PlusIcon, StopIcon } from "./Icons";
import styles from "./TimerWindow.module.scss";
import { formatTime } from "./utils";

type TimerWindowProps = {
    runPause: () => void;
    addTime: () => void;
    onClose: () => void;
    stop: () => void;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement>, type: "min" | "sec") => void;
    onBlur: () => void;
    onFocus: () => void;
    status: "run" | "pause" | "stop";
    min: string;
    sec: string;
    readonly: boolean;
};

export const TimerWindow = ({
    runPause,
    addTime,
    onClose,
    stop,
    onInputChange,
    onBlur,
    onFocus,
    status,
    min,
    sec,
    readonly,
}: TimerWindowProps) => {
    const { t } = useTranslation();
    const onMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onInputChange(e, "min");
    };

    const onSecChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onInputChange(e, "sec");
    };

    const formatValue = (value: string) => (readonly ? formatTime(Number(value)) : value);

    return (
        <div className={`${styles.timerWindow} ${styles.timerWindowReady}`}>
            <div className={styles.timerWindowTime}>
                <input
                    type="text"
                    maxLength={2}
                    className={`${styles.timerWindowTimeInput} ${styles.timerWindowTimeInputMin}`}
                    value={formatValue(min)}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onChange={onMinChange}
                    readOnly={readonly}
                    inputMode="numeric"
                />
                <div className={styles.timerWindowTimeDots}>:</div>
                <input
                    type="text"
                    maxLength={2}
                    className={`${styles.timerWindowTimeInput}`}
                    value={formatValue(sec)}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onChange={onSecChange}
                    readOnly={readonly}
                    inputMode="numeric"
                />
            </div>

            <div className={styles.timerWindowControlls}>
                <div className={styles.timerWindowControllsTop}>
                    <div className={`${styles.timerWindowControllsTopItem}`} onClick={runPause}>
                        {status === "run" ? <PauseIcon /> : <PlayIcon />}
                    </div>
                    {(status === "run" || status === "pause") && (
                        <div
                            className={`${styles.timerWindowControllsTopItem} ${styles.timerWindowControllsTopItemStop} ${
                                styles.timerWindowControllsTopItemStopShow
                            }`}
                            onClick={stop}
                        >
                            <StopIcon />
                        </div>
                    )}
                </div>
                <div className={styles.timerWindowControllsPlus} onClick={addTime}>
                    <PlusIcon />
                    <p>{t("timer.addMinute")}</p>
                </div>
            </div>

            <div className={styles.timerWindowClose} onClick={onClose}>
                <CloseIcon />
            </div>
        </div>
    );
};
